// WEBAPP-021: Supporto infinite scroll
import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { Search, Plus, Dumbbell, Filter, X, Loader2, Image, Eye, Edit, LayoutGrid, List } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import MuscleBadges from './MuscleBadges';
import useMuscleGroups from '../../hooks/useMuscleGroups';

/**
 * Nuovo selettore di esercizi che usa il sistema muscolare avanzato
 * WEBAPP-021: Supporta infinite scroll per paginazione
 * WEBAPP-001: Fix focus loss nel campo ricerca (controlled component + useLayoutEffect)
 * 
 * @param {string} searchTerm - CONTROLLED: Termine di ricerca dal padre
 * @param {Array} exercises - Array di esercizi disponibili
 * @param {Array} selectedExercises - Array di esercizi già selezionati
 * @param {function} onSelectExercise - Callback quando un esercizio viene selezionato
 * @param {function} onShowAddExerciseForm - Callback per mostrare il form di creazione esercizio
 * @param {boolean} loading - Se true, mostra loading spinner
 * @param {boolean} hasMore - Se true, ci sono più esercizi da caricare (WEBAPP-021)
 * @param {function} onLoadMore - Callback per caricare più esercizi (WEBAPP-021)
 */
const NewExerciseSelector = ({ 
    searchTerm = '', // WEBAPP-001: CONTROLLED from parent (non più state locale)
    exercises = [], 
    selectedExercises = [],
    onSelectExercise, 
    onShowAddExerciseForm, 
    loading = false,
    hasMore = false,
    onLoadMore = null,
    onFiltersChange = null, // Callback per notificare cambiamenti filtri al padre
    onSearchChange = null, // Callback per notificare cambiamenti ricerca al padre
    /** Modali gym: input e select filtri con sfondo viola (#484080) invece di gray-700 */
    gymExerciseSelector = false,
}) => {
    const { t } = useTranslation();
    const { muscleGroups, loading: loadingMuscles } = useMuscleGroups();
    
    // State per filtri e ricerca - solo attrezzatura e muscolo primario come in archivio
    // WEBAPP-001: searchTerm rimosso da qui, ora è prop controlled dal padre
    const [selectedEquipment, setSelectedEquipment] = useState('');
    const [selectedPrimaryMuscle, setSelectedPrimaryMuscle] = useState('');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
    
    // WEBAPP-GYM-001: Stato locale per il termine di ricerca (sincronizzato con prop)
    const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
    
    // Sincronizza lo stato locale quando cambia la prop
    useEffect(() => {
        setLocalSearchTerm(searchTerm);
    }, [searchTerm]);
    
    // WEBAPP-GYM-002: Funzione per resettare tutti i filtri
    const handleResetFilters = useCallback(() => {
        setSelectedEquipment('');
        setSelectedPrimaryMuscle('');
        // Notifica il padre che i filtri sono stati resettati
        if (onFiltersChange) {
            onFiltersChange({
                equipment: undefined,
                primaryMuscle: undefined
            });
        }
    }, [onFiltersChange]);
    
    // WEBAPP-GYM-002: Calcola se ci sono filtri attivi
    const hasActiveFilters = useMemo(() => {
        return (selectedEquipment && selectedEquipment.trim() !== '') || 
               (selectedPrimaryMuscle && selectedPrimaryMuscle.trim() !== '');
    }, [selectedEquipment, selectedPrimaryMuscle]);
    
    // WEBAPP-001: Refs per preservare focus e cursor position
    const searchInputRef = useRef(null);
    const wasFocusedRef = useRef(false);
    const cursorPositionRef = useRef(0);
    
    // State per opzioni filtri (caricate da API)
    const { getAuthenticatedApi } = useAuth();
    const api = getAuthenticatedApi();
    const [filterOptions, setFilterOptions] = useState({
        equipments: [],
        primaryMuscles: []
    });
    const [loadingFilters, setLoadingFilters] = useState(true);

    // WEBAPP-021: Infinite scroll ref e preservazione scroll
    const observerRef = useRef();
    const scrollContainerRef = useRef(null);
    const savedScrollPositionRef = useRef(null);
    const savedScrollHeightRef = useRef(null);
    const isRestoringScrollRef = useRef(false);
    const previousExercisesLengthRef = useRef(exercises.length);
    const isInitialMountRef = useRef(true);
    const mutationObserverRef = useRef(null);
    const lastVisibleExerciseIdRef = useRef(null);
    
    // WEBAPP-001: useLayoutEffect per ripristinare focus PRIMA del browser paint
    // Questo previene il flicker visivo ed è più affidabile di useEffect
    // TRIGGER: sia quando searchTerm cambia CHE quando exercises cambiano (API response)
    useLayoutEffect(() => {
        if (wasFocusedRef.current && searchInputRef.current) {
            const input = searchInputRef.current;
            
            // Ripristina focus solo se è stato perso
            if (document.activeElement !== input) {
                input.focus();
                
                // WEBAPP-001: Ripristina cursor position (CRITICO per UX)
                // Senza questo, il cursore tornerebbe alla fine del testo
                const position = cursorPositionRef.current;
                input.setSelectionRange(position, position);
            }
        }
    }, [searchTerm, exercises.length]); // ⚡ Trigger anche quando API risponde con nuovi esercizi!
    
    // Carica opzioni filtri da API (come in ExerciseList)
    useEffect(() => {
        const loadFilters = async () => {
            try {
                setLoadingFilters(true);
                
                // Passa i filtri attivi all'API per ottenere solo le opzioni rilevanti
                const params = {};
                if (selectedEquipment && selectedEquipment.trim() !== '') {
                    params.attrezzatura = selectedEquipment.trim();
                }
                if (selectedPrimaryMuscle && selectedPrimaryMuscle.trim() !== '') {
                    params.primary_muscle = selectedPrimaryMuscle.trim();
                }
                if (searchTerm && searchTerm.trim().length >= 2) {
                    params.search = searchTerm.trim();
                }
                
                const response = await api.get('/exercise_filters.php', { params });
                if (response.data && response.data.success) {
                    setFilterOptions({
                        equipments: response.data.equipments || [],
                        primaryMuscles: response.data.primary_muscles || []
                    });
                }
            } catch (err) {
                console.error('Errore nel caricamento dei filtri:', err);
            } finally {
                setLoadingFilters(false);
            }
        };
        
        loadFilters();
    }, [api, selectedEquipment, selectedPrimaryMuscle, searchTerm]);
    
    // I filtri vengono applicati lato server, quindi restituiamo direttamente gli esercizi
    // Non serve filtrare lato client perché usePaginatedExercises gestisce tutto lato server
    const filteredExercises = useMemo(() => {
        if (!exercises || !Array.isArray(exercises)) return [];
        return exercises;
    }, [exercises]);
    
    // WEBAPP-021: Wrapper per onLoadMore che salva la posizione scroll prima di caricare
    const handleLoadMore = useCallback(() => {
        if (scrollContainerRef.current && onLoadMore) {
            // Salva posizione scroll PRIMA di chiamare onLoadMore
            const container = scrollContainerRef.current;
            const currentScrollTop = container.scrollTop;
            const currentScrollHeight = container.scrollHeight;
            const scrollBottom = currentScrollHeight - currentScrollTop - container.clientHeight;
            
            // Trova l'ultimo esercizio visibile
            const exerciseElements = container.querySelectorAll('[data-exercise-id]');
            let lastVisibleId = null;
            exerciseElements.forEach(el => {
                const rect = el.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();
                if (rect.top >= containerRect.top && rect.top <= containerRect.bottom) {
                    lastVisibleId = el.getAttribute('data-exercise-id');
                }
            });
            lastVisibleExerciseIdRef.current = lastVisibleId;
            
            // Salva la distanza dal fondo invece della posizione assoluta
            savedScrollPositionRef.current = scrollBottom;
            savedScrollHeightRef.current = currentScrollHeight;
            isRestoringScrollRef.current = true;
            
            // Chiama onLoadMore immediatamente (non in requestAnimationFrame)
            // per evitare che React resetti lo scroll prima che venga chiamato
            onLoadMore();
        }
    }, [onLoadMore]);
    
    // WEBAPP-021: Setup MutationObserver per preservare scroll quando vengono aggiunti nuovi elementi
    useEffect(() => {
        if (!scrollContainerRef.current) return;
        
        const container = scrollContainerRef.current;
        
        // Crea MutationObserver per rilevare quando vengono aggiunti nuovi elementi
        mutationObserverRef.current = new MutationObserver((mutations) => {
            if (isRestoringScrollRef.current && savedScrollPositionRef.current !== null && savedScrollHeightRef.current !== null) {
                // Controlla se sono stati aggiunti nuovi nodi
                const hasNewNodes = mutations.some(mutation => mutation.addedNodes.length > 0);
                
                if (hasNewNodes) {
                    // Preserva lo scroll immediatamente, senza aspettare requestAnimationFrame
                    const currentScrollHeight = container.scrollHeight;
                    const scrollDiff = currentScrollHeight - savedScrollHeightRef.current;
                    
                    if (scrollDiff > 0) {
                        // Mantieni la stessa distanza dal fondo
                        const newScrollTop = currentScrollHeight - savedScrollPositionRef.current - container.clientHeight;
                        container.scrollTop = Math.max(0, newScrollTop);
                        
                        // Aggiorna savedScrollHeightRef per il prossimo ciclo
                        savedScrollHeightRef.current = currentScrollHeight;
                    }
                    
                    // Usa anche doppio requestAnimationFrame come backup per assicurarsi che lo scroll sia preservato
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            if (container && isRestoringScrollRef.current) {
                                const currentScrollHeight = container.scrollHeight;
                                const scrollDiff = currentScrollHeight - savedScrollHeightRef.current;
                                
                                if (scrollDiff > 0) {
                                    // Mantieni la stessa distanza dal fondo
                                    const newScrollTop = currentScrollHeight - savedScrollPositionRef.current - container.clientHeight;
                                    container.scrollTop = Math.max(0, newScrollTop);
                                    
                                    // Aggiorna savedScrollHeightRef per il prossimo ciclo
                                    savedScrollHeightRef.current = currentScrollHeight;
                                }
                            }
                        });
                    });
                }
            }
        });
        
        // Osserva aggiunte di nodi figli
        mutationObserverRef.current.observe(container, {
            childList: true,
            subtree: true
        });
        
        return () => {
            if (mutationObserverRef.current) {
                mutationObserverRef.current.disconnect();
            }
        };
    }, []);
    
    // WEBAPP-021: Previeni reset scroll durante il render quando stiamo caricando nuovi elementi
    useLayoutEffect(() => {
        // Salta il primo render (mount iniziale)
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            previousExercisesLengthRef.current = exercises.length;
            return;
        }
        
        // Controlla se sono stati aggiunti nuovi esercizi
        const exercisesAdded = exercises.length > previousExercisesLengthRef.current;
        previousExercisesLengthRef.current = exercises.length;
        
        if (scrollContainerRef.current && isRestoringScrollRef.current && savedScrollPositionRef.current !== null && savedScrollHeightRef.current !== null && exercisesAdded) {
            const container = scrollContainerRef.current;
            
            // Prova prima a usare l'ultimo esercizio visibile per preservare lo scroll
            if (lastVisibleExerciseIdRef.current) {
                const lastVisibleElement = container.querySelector(`[data-exercise-id="${lastVisibleExerciseIdRef.current}"]`);
                if (lastVisibleElement) {
                    // Scrolla l'elemento in vista mantenendo la stessa posizione relativa
                    lastVisibleElement.scrollIntoView({ behavior: 'auto', block: 'start' });
                    // Aggiusta la posizione per mantenere la distanza dal fondo
                    const currentScrollHeight = container.scrollHeight;
                    const newScrollTop = currentScrollHeight - savedScrollPositionRef.current - container.clientHeight;
                    container.scrollTop = Math.max(0, newScrollTop);
                    savedScrollHeightRef.current = currentScrollHeight;
                    return;
                }
            }
            
            // Fallback: preserva lo scroll IMMEDIATAMENTE, senza aspettare requestAnimationFrame
            // Questo è critico per prevenire il reset dello scroll durante il re-render
            const currentScrollHeight = container.scrollHeight;
            const scrollDiff = currentScrollHeight - savedScrollHeightRef.current;
            
            if (scrollDiff > 0) {
                // Mantieni la stessa distanza dal fondo
                const newScrollTop = currentScrollHeight - savedScrollPositionRef.current - container.clientHeight;
                container.scrollTop = Math.max(0, newScrollTop);
                // Aggiorna savedScrollHeightRef per il prossimo ciclo
                savedScrollHeightRef.current = currentScrollHeight;
            }
            
            // Usa anche requestAnimationFrame come backup per assicurarsi che lo scroll sia preservato
            requestAnimationFrame(() => {
                if (container && isRestoringScrollRef.current) {
                    const currentScrollHeight = container.scrollHeight;
                    const scrollDiff = currentScrollHeight - savedScrollHeightRef.current;
                    
                    if (scrollDiff > 0) {
                        const newScrollTop = currentScrollHeight - savedScrollPositionRef.current - container.clientHeight;
                        container.scrollTop = Math.max(0, newScrollTop);
                        savedScrollHeightRef.current = currentScrollHeight;
                    }
                }
            });
        }
    }, [filteredExercises.length, exercises.length]);
    
    // WEBAPP-021: Aggressivamente preserva scroll quando esercizi cambiano
    useEffect(() => {
        // Salta se è il mount iniziale o se non stiamo restaurando lo scroll
        if (isInitialMountRef.current || !isRestoringScrollRef.current) {
            return;
        }
        
        if (scrollContainerRef.current && savedScrollPositionRef.current !== null && savedScrollHeightRef.current !== null) {
            const container = scrollContainerRef.current;
            
            // Usa doppio requestAnimationFrame per preservare lo scroll immediatamente
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (container && isRestoringScrollRef.current) {
                        const currentScrollHeight = container.scrollHeight;
                        const scrollDiff = currentScrollHeight - savedScrollHeightRef.current;
                        
                        if (scrollDiff > 0) {
                            // Mantieni la stessa distanza dal fondo
                            const newScrollTop = currentScrollHeight - savedScrollPositionRef.current - container.clientHeight;
                            container.scrollTop = Math.max(0, newScrollTop);
                            savedScrollHeightRef.current = currentScrollHeight;
                        }
                    }
                });
            });
            
            // Usa un intervallo per preservare lo scroll durante il caricamento
            const interval = setInterval(() => {
                if (container && isRestoringScrollRef.current) {
                    const newScrollHeight = container.scrollHeight;
                    if (newScrollHeight > savedScrollHeightRef.current) {
                        const newScrollTop = newScrollHeight - savedScrollPositionRef.current - container.clientHeight;
                        container.scrollTop = Math.max(0, newScrollTop);
                        savedScrollHeightRef.current = newScrollHeight;
                    }
                }
            }, 16); // ~60fps
            
            // Pulisci intervallo dopo 3 secondi
            const timeout = setTimeout(() => {
                clearInterval(interval);
                // Reset flag dopo che tutto è stato caricato
                if (isRestoringScrollRef.current) {
                    setTimeout(() => {
                        isRestoringScrollRef.current = false;
                        savedScrollPositionRef.current = null;
                        savedScrollHeightRef.current = null;
                    }, 200);
                }
            }, 3000);
            
            return () => {
                clearInterval(interval);
                clearTimeout(timeout);
            };
        }
    }, [exercises.length, filteredExercises.length]);
    
    // WEBAPP-021: Ripristina posizione scroll quando il loading finisce
    useEffect(() => {
        if (!loading && isRestoringScrollRef.current && savedScrollPositionRef.current !== null && savedScrollHeightRef.current !== null) {
            // Reset flag dopo che tutto è stato caricato
            setTimeout(() => {
                isRestoringScrollRef.current = false;
                savedScrollPositionRef.current = null;
                savedScrollHeightRef.current = null;
            }, 200);
        }
    }, [loading]);
    
    const lastExerciseRef = useCallback(node => {
        if (loading || !hasMore || !handleLoadMore) return;
        if (observerRef.current) observerRef.current.disconnect();
        observerRef.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && handleLoadMore) {
                handleLoadMore();
            }
        });
        if (node) observerRef.current.observe(node);
    }, [loading, hasMore, handleLoadMore]);

    // Non serve più calcolare muscoli e attrezzature dagli esercizi,
    // vengono caricati dall'API exercise_filters.php

    // Funzione per ottenere URL immagine
    const getImageUrl = (exercise) => {
        if (exercise.immagine_nome) {
            return `/api/serve_image.php?filename=${encodeURIComponent(exercise.immagine_nome)}`;
        }
        return null;
    };

    // Funzione per verificare se un esercizio è già selezionato
    const isExerciseSelected = (exercise) => {
        return selectedExercises.some(ex => 
            ex.id == exercise.id || 
            ex.esercizio_id == exercise.id ||
            ex.id == exercise.esercizio_id
        );
    };

    // Funzione per gestire selezione esercizio
    const handleSelectExercise = (exercise) => {
        if (onSelectExercise) {
            onSelectExercise(exercise);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    const searchInputClass = gymExerciseSelector
        ? 'w-full rounded-xl border border-[#dad8e5] bg-white py-2.5 pl-9 pr-11 text-sm text-[#3a3a3a] shadow-sm placeholder:text-[#3a3a3a]/45 focus:border-[#372d83] focus:outline-none focus:ring-2 focus:ring-[#372d83]/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-white/45 dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30'
        : `w-full rounded-md border border-gray-300 py-1 pl-7 pr-9 text-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white`;

    const filterSelectClass = gymExerciseSelector
        ? 'min-w-[10.5rem] flex-1 rounded-xl border border-[#dad8e5] bg-white px-3 py-2.5 text-sm font-medium text-[#3a3a3a] shadow-sm focus:border-[#372d83] focus:outline-none focus:ring-2 focus:ring-[#372d83]/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:ring-indigo-500/30 sm:min-w-[11.5rem] sm:flex-none'
        : 'rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white';

    const searchIconClass = gymExerciseSelector
        ? 'pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#372d83] dark:text-gray-300'
        : 'pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400';

    const searchSubmitBtnClass = gymExerciseSelector
        ? 'absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg bg-[#372d83] text-white shadow-sm transition hover:brightness-110 dark:bg-gray-800'
        : 'absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-indigo-600 transition-colors hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/20';

    return (
        <div className="flex h-full flex-col overflow-hidden">
            {/* Header con ricerca, filtri e pulsante aggiungi */}
            <div
                className={`mb-4 flex flex-shrink-0 flex-wrap items-center gap-2 ${
                    gymExerciseSelector
                        ? 'rounded-2xl border border-[#dad8e5] bg-[#eeebf5] p-3 shadow-sm dark:border-white/10 dark:bg-gray-900'
                        : 'pt-3'
                }`}
            >
                {/* Ricerca - con bottone per cercare */}
                <div
                    className={`relative flex min-w-0 flex-shrink items-center ${gymExerciseSelector ? 'min-w-[200px] flex-1 basis-[min(100%,320px)]' : ''}`}
                    style={gymExerciseSelector ? undefined : { maxWidth: '240px' }}
                >
                    <Search className={searchIconClass} aria-hidden />
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={localSearchTerm}
                        onChange={(e) => {
                            const { value, selectionStart } = e.target;
                            
                            // WEBAPP-001: Salva cursor position
                            cursorPositionRef.current = selectionStart;
                            
                            // WEBAPP-GYM-001: Aggiorna solo lo stato locale, non il padre
                            setLocalSearchTerm(value);
                        }}
                        onKeyDown={(e) => {
                            // WEBAPP-GYM-001: Gestisce tasto Invio per confermare ricerca
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                // Notifica il padre per avviare la ricerca
                                if (onSearchChange) {
                                    onSearchChange(localSearchTerm);
                                }
                            }
                        }}
                        onFocus={() => {
                            // WEBAPP-001: Traccia che l'input ha il focus
                            wasFocusedRef.current = true;
                        }}
                        onBlur={() => {
                            // WEBAPP-001: Traccia che l'input ha perso il focus
                            wasFocusedRef.current = false;
                        }}
                        placeholder={t('exerciseSelector.searchPlaceholder', 'Cerca e premi Invio...')}
                        className={searchInputClass}
                    />
                    {/* WEBAPP-GYM-001: Bottone per avviare la ricerca */}
                    <button
                        type="button"
                        onClick={() => {
                            if (onSearchChange) {
                                onSearchChange(localSearchTerm);
                            }
                        }}
                        className={searchSubmitBtnClass}
                        title="Cerca"
                    >
                        <Search className="h-4 w-4" />
                    </button>
                </div>

                {/* Filtri avanzati - solo attrezzatura e muscolo primario come in archivio */}
                <select
                    value={selectedEquipment}
                    onChange={(e) => {
                        const value = e.target.value;
                        setSelectedEquipment(value);
                        // Notifica il padre del cambio filtro
                        if (onFiltersChange) {
                            onFiltersChange({
                                equipment: value && value.trim() !== '' ? value.trim() : undefined,
                                primaryMuscle: selectedPrimaryMuscle && selectedPrimaryMuscle.trim() !== '' ? selectedPrimaryMuscle.trim() : undefined
                            });
                        }
                    }}
                    className={filterSelectClass}
                    title="Filtra per attrezzatura"
                    disabled={loadingFilters}
                >
                    <option value="">Tutte le attrezzature</option>
                    {filterOptions.equipments.map(equipment => (
                        <option key={equipment} value={equipment}>{equipment}</option>
                    ))}
                </select>

                <select
                    value={selectedPrimaryMuscle}
                    onChange={(e) => {
                        const value = e.target.value;
                        setSelectedPrimaryMuscle(value);
                        // Notifica il padre del cambio filtro
                        if (onFiltersChange) {
                            onFiltersChange({
                                equipment: selectedEquipment && selectedEquipment.trim() !== '' ? selectedEquipment.trim() : undefined,
                                primaryMuscle: value && value.trim() !== '' ? value.trim() : undefined
                            });
                        }
                    }}
                    className={filterSelectClass}
                    title="Filtra per muscolo primario"
                    disabled={loadingFilters}
                >
                    <option value="">Tutti i muscoli</option>
                    {filterOptions.primaryMuscles.map(muscle => (
                        <option key={muscle} value={muscle}>{muscle}</option>
                    ))}
                </select>

                {/* WEBAPP-GYM-002: Bottone Reset Filtri - visibile solo se ci sono filtri attivi */}
                {hasActiveFilters && (
                    <button
                        type="button"
                        onClick={handleResetFilters}
                        className={`flex items-center gap-1 whitespace-nowrap text-xs font-medium transition-colors ${
                            gymExerciseSelector
                                ? 'rounded-lg border border-red-200 bg-white px-2.5 py-2 text-red-600 hover:bg-red-50 dark:border-red-500/40 dark:bg-gray-800/40 dark:text-red-300 dark:hover:bg-red-900/25'
                                : 'rounded-md border border-red-300 px-2 py-1.5 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300'
                        }`}
                        title="Cancella tutti i filtri"
                    >
                        <X className="h-3.5 w-3.5" />
                        <span>Reset</span>
                    </button>
                )}

                {/* Pulsante Crea Nuovo */}
                <button
                    type="button"
                    onClick={onShowAddExerciseForm}
                    className={
                        gymExerciseSelector
                            ? 'inline-flex items-center gap-1.5 whitespace-nowrap rounded-xl border border-[#bca8d1] bg-gradient-to-r from-[#372d83] to-[#554b9f] px-4 py-2.5 text-xs font-semibold text-white shadow-md transition hover:brightness-110 dark:border-white/15 dark:from-gray-800 dark:to-gray-900'
                            : 'inline-flex items-center gap-1 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-700 dark:text-indigo-400 dark:hover:bg-indigo-900/20 dark:hover:text-indigo-300'
                    }
                    title="Crea nuovo esercizio"
                >
                    <Plus className={gymExerciseSelector ? 'h-4 w-4' : 'h-3.5 w-3.5'} strokeWidth={2.5} />
                    <span>Nuovo</span>
                </button>
                
                {/* Vista griglia / lista */}
                <div
                    className={
                        gymExerciseSelector
                            ? 'ml-0 inline-flex w-full justify-end sm:ml-auto sm:w-auto rounded-xl border border-[#dad8e5] bg-white/90 p-1 shadow-inner dark:border-white/10 dark:bg-gray-800/50'
                            : 'flex space-x-1'
                    }
                    role="group"
                    aria-label={t('exerciseSelector.viewMode', 'Modalità visualizzazione')}
                >
                    <button
                        type="button"
                        onClick={() => setViewMode('grid')}
                        className={
                            gymExerciseSelector
                                ? `inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-all ${
                                      viewMode === 'grid'
                                          ? 'bg-[#eeebf5] text-[#372d83] shadow-sm dark:bg-gray-900 dark:text-white'
                                          : 'text-[#3a3a3a]/65 hover:bg-[#eeebf5]/80 hover:text-[#372d83] dark:text-white/55 dark:hover:bg-white/5 dark:hover:text-white'
                                  }`
                                : `rounded-md px-2 py-1 text-xs ${
                                      viewMode === 'grid'
                                          ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                                          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                                  }`
                        }
                        title="Griglia"
                    >
                        <LayoutGrid className="h-4 w-4 shrink-0" aria-hidden />
                        <span>Grid</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setViewMode('list')}
                        className={
                            gymExerciseSelector
                                ? `inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-all ${
                                      viewMode === 'list'
                                          ? 'bg-[#eeebf5] text-[#372d83] shadow-sm dark:bg-gray-900 dark:text-white'
                                          : 'text-[#3a3a3a]/65 hover:bg-[#eeebf5]/80 hover:text-[#372d83] dark:text-white/55 dark:hover:bg-white/5 dark:hover:text-white'
                                  }`
                                : `rounded-md px-2 py-1 text-xs ${
                                      viewMode === 'list'
                                          ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                                          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                                  }`
                        }
                        title="Lista"
                    >
                        <List className="h-4 w-4 shrink-0" aria-hidden />
                        <span>Lista</span>
                    </button>
                </div>
            </div>

            {/* Lista esercizi */}
            <div 
                ref={(node) => {
                    if (node !== scrollContainerRef.current) {
                        scrollContainerRef.current = node;
                        // WEBAPP-021: Preserva scroll immediatamente quando il ref viene assegnato
                        if (node && isRestoringScrollRef.current && savedScrollPositionRef.current !== null && savedScrollHeightRef.current !== null) {
                            // Usa requestAnimationFrame per assicurarsi che il DOM sia pronto
                            // Usa doppio requestAnimationFrame per assicurarsi che il DOM sia completamente aggiornato
                            requestAnimationFrame(() => {
                                requestAnimationFrame(() => {
                                    if (node && isRestoringScrollRef.current) {
                                        const currentScrollHeight = node.scrollHeight;
                                        const scrollDiff = currentScrollHeight - savedScrollHeightRef.current;
                                        if (scrollDiff > 0) {
                                            // Mantieni la stessa distanza dal fondo
                                            const newScrollTop = currentScrollHeight - savedScrollPositionRef.current - node.clientHeight;
                                            node.scrollTop = Math.max(0, newScrollTop);
                                            savedScrollHeightRef.current = currentScrollHeight;
                                        }
                                    }
                                });
                            });
                        }
                    }
                }}
                className="flex-1 min-h-0 overflow-y-auto"
                style={{
                    // WEBAPP-021: Previeni reset scroll e scroll chaining
                    scrollBehavior: 'auto',
                    overscrollBehavior: 'contain'
                }}
                onWheel={(e) => {
                    // WEBAPP-021: Previeni propagazione scroll al container padre
                    const container = e.currentTarget;
                    const { scrollTop, scrollHeight, clientHeight } = container;
                    const isAtTop = scrollTop === 0;
                    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
                    
                    // Se siamo in cima o in fondo, previeni la propagazione solo se necessario
                    if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
                        // Permetti scroll chaining solo se siamo ai limiti
                    } else {
                        // Previeni scroll chaining quando scrolliamo all'interno
                        e.stopPropagation();
                    }
                }}
            >
                {filteredExercises.length === 0 ? (
                    <div className="text-center py-8">
                        <Dumbbell className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                        {!searchTerm && !selectedEquipment && !selectedPrimaryMuscle ? (
                            <p className="text-gray-500 dark:text-gray-400">
                                Inizia a cercare o applica dei filtri per vedere gli esercizi
                            </p>
                        ) : (
                            <p className="text-gray-500 dark:text-gray-400">
                                {t('exerciseSelector.noExercisesFound', 'Nessun esercizio trovato')}
                            </p>
                        )}
                    </div>
                ) : (
                    viewMode === 'grid' ? (
                        // Visualizzazione Grid stile Instagram
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                            {filteredExercises.map((exercise, index) => {
                                const isSelected = isExerciseSelected(exercise);
                                const isLast = index === filteredExercises.length - 1;
                                const imageUrl = getImageUrl(exercise);
                                
                                return (
                                    <div
                                        key={exercise.id}
                                        data-exercise-id={exercise.id}
                                        ref={isLast && hasMore ? lastExerciseRef : null}
                                        onClick={() => handleSelectExercise(exercise)}
                                        className={`cursor-pointer rounded-lg border overflow-hidden transition-all hover:shadow-lg ${
                                            isSelected 
                                                ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                                                : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-500 bg-white dark:bg-gray-800'
                                        }`}
                                    >
                                        {/* Immagine grande stile Instagram */}
                                        <div className="relative aspect-square bg-gray-100 dark:bg-gray-700 overflow-hidden group">
                                            {imageUrl ? (
                                                <img
                                                    src={imageUrl}
                                                    alt={exercise.nome}
                                                    className="w-full h-full object-cover select-none"
                                                    onContextMenu={(e) => e.preventDefault()}
                                                    draggable={false}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        const placeholder = e.target.nextElementSibling;
                                                        if (placeholder) placeholder.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div className={`${imageUrl ? 'hidden' : 'flex'} w-full h-full items-center justify-center`}>
                                                <Image className="h-16 w-16 text-gray-400" />
                                            </div>
                                            
                                            {/* Badge "Selezionato" */}
                                            {isSelected && (
                                                <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1.5 z-10">
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                            )}
                                            
                                            {/* Overlay con pulsanti al hover */}
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectExercise(exercise);
                                                    }}
                                                    className="p-2 bg-white/90 dark:bg-gray-800/90 rounded-full text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800 transition-colors"
                                                    title={isSelected ? "Deseleziona" : "Seleziona esercizio"}
                                                >
                                                    {isSelected ? (
                                                        <X className="h-4 w-4" />
                                                    ) : (
                                                        <Plus className="h-4 w-4" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {/* Nome e info sotto l'immagine */}
                                        <div className="p-3">
                                            <h3 
                                                className={`font-medium text-sm mb-2 line-clamp-2 ${
                                                    isSelected ? 'text-green-700 dark:text-green-300' : 'text-gray-900 dark:text-white'
                                                }`}
                                                title={exercise.nome}
                                            >
                                                {exercise.nome}
                                            </h3>
                                            
                                            {/* Badge muscoli/attrezzatura compatti */}
                                            <div className="space-y-1">
                                                {exercise.primary_muscle ? (
                                                    <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
                                                        {exercise.primary_muscle.name}
                                                    </div>
                                                ) : exercise.gruppo_muscolare ? (
                                                    <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
                                                        {exercise.gruppo_muscolare}
                                                    </div>
                                                ) : null}
                                                
                                                <div className="flex flex-wrap items-center gap-1">
                                                    {exercise.is_cardio === 1 && (
                                                        <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded">
                                                            Cardio
                                                        </span>
                                                    )}
                                                    {exercise.attrezzatura && (
                                                        <span className="text-xs text-gray-500 dark:text-gray-400 truncate" title={exercise.attrezzatura}>
                                                            🔧 {exercise.attrezzatura.length > 12 ? exercise.attrezzatura.substring(0, 12) + '...' : exercise.attrezzatura}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        // Visualizzazione Lista originale
                        <div className="space-y-3">
                            {filteredExercises.map((exercise, index) => {
                                const isSelected = isExerciseSelected(exercise);
                                const isLast = index === filteredExercises.length - 1;
                                return (
                                    <div
                                        key={exercise.id}
                                        data-exercise-id={exercise.id}
                                        ref={isLast && hasMore ? lastExerciseRef : null}
                                        onClick={() => handleSelectExercise(exercise)}
                                        className={`cursor-pointer rounded-lg border-2 transition-all p-4 hover:shadow-md ${
                                            isSelected 
                                                ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                                                : 'border-gray-200 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-indigo-500 bg-white dark:bg-gray-800'
                                        }`}
                                    >
                                        <div className="flex items-start space-x-4">
                                            {/* Immagine esercizio - quadrata a sinistra */}
                                            <div className="w-20 h-20 flex-shrink-0 relative">
                                                {getImageUrl(exercise) ? (
                                                    <img
                                                        src={getImageUrl(exercise)}
                                                        alt={exercise.nome}
                                                        className="w-full h-full rounded-lg object-cover bg-gray-100 dark:bg-gray-700 select-none"
                                                        onContextMenu={(e) => e.preventDefault()}
                                                        draggable={false}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                <div 
                                                    className="w-full h-full rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center"
                                                    style={{ display: 'none' }}
                                                >
                                                    <Dumbbell className="h-8 w-8 text-gray-400" />
                                                </div>
                                                
                                                {/* Badge "Selezionato" sull'immagine */}
                                                {isSelected && (
                                                    <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full p-1">
                                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Contenuto esercizio - a destra */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between mb-1">
                                                    <h3 className={`font-medium text-base ${
                                                        isSelected ? 'text-green-700 dark:text-green-300' : 'text-gray-900 dark:text-white'
                                                    }`}>
                                                        {exercise.nome}
                                                    </h3>
                                                    {isSelected && (
                                                        <span className="ml-2 px-2 py-1 text-xs font-semibold bg-green-500 text-white rounded-full">
                                                            ✓ Selezionato
                                                        </span>
                                                    )}
                                                </div>
                                            
                                            {exercise.descrizione && (
                                                <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 line-clamp-2">
                                                    {exercise.descrizione}
                                                </p>
                                            )}

                                            {/* Badge muscoli */}
                                            <div className="mb-3">
                                                <MuscleBadges
                                                    primaryMuscle={exercise.primary_muscle}
                                                    secondaryMuscles={exercise.secondary_muscles}
                                                    variant="compact"
                                                    showCategory={false}
                                                />
                                            </div>

                                            {/* Info aggiuntive */}
                                            <div className="flex items-center space-x-2 text-xs">
                                                {exercise.attrezzatura && (
                                                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                                                        {exercise.attrezzatura}
                                                    </span>
                                                )}
                                                {exercise.is_cardio === 1 && (
                                                    <span className="px-2 py-1 bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 rounded-full">
                                                        Cardio
                                                    </span>
                                                )}
                                            </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )
                )}
                
                {/* WEBAPP-021: Loading indicator per infinite scroll */}
                {loading && hasMore && (
                    <div className="flex justify-center py-4">
                        <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                    </div>
                )}
                
                {/* WEBAPP-021: Fine lista indicator */}
                {!hasMore && filteredExercises.length > 0 && !loading && (
                    <p className="text-center text-xs text-gray-400 dark:text-gray-500 py-2">
                        {t('common.endOfList', 'Fine della lista')}
                    </p>
                )}
            </div>
        </div>
    );
};

export default NewExerciseSelector;
